import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";

const Skills = () => {
  const skillGroups = useMemo(
    () => [
      {
        title: "Languages",
        subtitle: "Core languages I write production code in",
        items: ["C", "Python", "JavaScript", "TypeScript", "HTML", "CSS", "SQL"],
        example: "From manual memory management in C to TypeScript-strict React Native — these are the languages I reach for when building real systems.",
      },
      {
        title: "Frameworks & Libraries",
        subtitle: "UI, mobile, and data-layer tools",
        items: ["ReactJS", "React Native / Expo", "NodeJS", "Expo Router", "TanStack Query", "Zustand"],
        example: "File-based routing, server-state caching with stale-time tuning, and zero-Provider Zustand stores — applied across both web and mobile.",
      },
      {
        title: "Databases & Backend",
        subtitle: "Data storage, querying, and security",
        items: ["Supabase", "PostgreSQL", "PostGIS", "Qdrant", "Row Level Security", "SQL RPCs"],
        example: "PostGIS geography queries, vector embeddings in Qdrant, and RLS as the sole security boundary — no application-layer guards needed.",
      },
      {
        title: "Cloud, DevOps & Tooling",
        subtitle: "Infrastructure, CI/CD, and developer tooling",
        items: ["Git", "Docker", "GitHub Actions", "Azure App Service", "Azure Application Insights", "SSH / Linux Servers"],
        example: "Zero-manual-intervention CI/CD pipelines to Azure, live request tracing with Application Insights, and containerized local environments.",
      },
      {
        title: "Automation & AI",
        subtitle: "Pipelines and agents that eliminate manual work",
        items: ["n8n", "AI Agents", "Gemini AI", "Qdrant Vector Search", "Microsoft Teams API", "Workflows"],
        example: "n8n agents that book calendar events, auto-publish LinkedIn drafts, and send emails by voice — saving measurable hours per day.",
      },
    ],
    []
  );

  const focusBullets = [
    "Production-scale geospatial systems: PostGIS ranking functions, spatial indexing, and zero-round-trip query design.",
    "Full-stack observability: Azure Application Insights instrumentation, live request tracing, and exception monitoring in production Node.js.",
    "AI-augmented data pipelines: semantic search with Qdrant vector DB, Gemini AI summarization, and automated content generation.",
  ];

  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className="skills-page">
      <motion.h3
        initial={{ y: 20, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        Skills
      </motion.h3>

      <div className="skills-accordion-layout">
        <div className="skills-accordion">
          {skillGroups.map((group, index) => {
            const isOpen = index === activeIndex;
            const panelId = `skills-panel-${index}`;
            const buttonId = `skills-button-${index}`;

            return (
              <div key={group.title} className="skills-acc-item">
                <button
                  id={buttonId}
                  type="button"
                  className="skills-acc-button"
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => setActiveIndex(isOpen ? -1 : index)}
                >
                  <span className="skills-acc-index" aria-hidden="true">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="skills-acc-text">
                    <span className="skills-acc-title">{group.title}</span>
                    <span className="skills-acc-subtitle">{group.subtitle}</span>
                  </span>

                  <span className={`skills-acc-chevron ${isOpen ? "open" : ""}`} aria-hidden="true">
                    ˅
                  </span>
                </button>

                <motion.div
                  id={panelId}
                  className="skills-acc-panel"
                  role="region"
                  aria-labelledby={buttonId}
                  initial={false}
                  animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                  style={{ overflow: "hidden" }}
                >
                  <div className="skills-acc-panel-inner">
                    <div className="skills-acc-example">{group.example}</div>
                    <div className="skills-chip-wrap">
                      {group.items.map((item) => (
                        <span key={item} className="skills-chip">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </div>
            );
          })}
        </div>

        <motion.aside
          className="skills-aside"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h4 className="skills-aside-title">Current focus</h4>
          <p className="skills-aside-subtitle">
            A short roadmap that ties my skills to projects I’m actively building.
          </p>

          <ul className="skills-bullets">
            {focusBullets.map((b) => (
              <li key={b} className="skills-bullet">
                <span className="skills-bullet-dot" aria-hidden="true" />
                {b}
              </li>
            ))}
          </ul>
        </motion.aside>
      </div>
    </section>
  );
};

export default Skills;
